import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import { LazyLoadEvent, MessageService } from 'primeng';
import { Title } from "@angular/platform-browser";
import {
    patientRefundList,
    refundData,
    refund_sanction_body
} from '../../../../../models/Accounts';
import {
    ErrorModel,
    PromiseResult,
    ServerError
} from '../../../../../models/PromiseResult';
import { PaginationModel } from '../../../../../models/util.models';
import { PatientRefundService } from '../../../../../services/accounts/patient.refund.service';
import {
    moment_date_format,
    UtilitiesService
} from '../../../../../utilities/utilities.service';
import { DoctorBreadcrumbService } from '../../../shared/breadcrumb.service';
import { TraceClassDecorator } from '@sentry/angular';
import { ProviderStateService } from 'src/app/services/provider-state.service';


@TraceClassDecorator()
@Component({
    selector: 'app-patient-refund',
    templateUrl: './patient-refund.component.html',
    styleUrls: ['./patient-refund.component.css'
        , '../../../../../../assets/css/custom-egd.css'
    ]
})
export class PatientRefundComponent implements OnInit {
    constructor(
        private readonly titleService: Title,
        private readonly patientRefundService: PatientRefundService,
        private readonly util: UtilitiesService,
        private readonly messageService: MessageService,
        private readonly breadcrumbService: DoctorBreadcrumbService,
        private readonly providerstateService: ProviderStateService
    ) {
    }

    response: PromiseResult;
    error: ErrorModel;
    serverError: ServerError;
    total_records: number = 0;
    pagination: PaginationModel = {
        page_no: 1,
        records_per_page: 10
    };
    loading: boolean = false;
    slug: string = "";
    refundList: patientRefundList[] = [];

    details_dialog_bool: boolean = false;
    sanction_dialog_bool: boolean = false;

    sanction_body: refund_sanction_body = {
        approve: null,
        refund_id: null
    };
    refD: refundData = new refundData();

    async ngOnInit(): Promise<void> {
        this.slug = this.providerstateService.getProviderSlug();
        this.breadcrumbService.setItems([
            { label: 'Dashboard', routerLink: [`/${this.slug}/doctor`] },
            { label: 'Accounts' },
            { label: 'Patient Refund' }
        ]);
        this.util.resetResponse(this.response, this.error, this.serverError);
        this.titleService.setTitle("Dashboard - Patient Refunds");
    }

    async fetchAll(event: LazyLoadEvent): Promise<void> {
        this.util.resetResponse(this.response, this.error, this.serverError);
        if (event != null) {
            this.pagination.page_no = event.first / event.rows + 1;
            this.pagination.records_per_page = event.rows;
        }
        this.loading = true;
        this.refundList = [];
        await this.patientRefundService
            .fetchAllRefunds(this.pagination)
            .toPromise()
            .then((result: PromiseResult) => {
                this.response = result;
                if (result.success) {
                    this.total_records = result.data.transactions.count;
                    const temp: patientRefundList[] = []
                    result.data.transactions.rows.forEach((ele, index) => {
                        temp.push({
                            sr_no:
                                index +
                                1 +
                                (this.pagination.page_no - 1) *
                                this.pagination.records_per_page,
                            refund_id: ele.refund_id,
                            refund_type: ele.refund_type,
                            refund_amount: ele.rendered_amount,
                            refund_actual_amount: ele.rendered_actual_amount,
                            refund_discounted_amount: ele.rendered_discount_amount,
                            audited_by_user_name:
                                ele.audited_by_user_name || 'N/A',
                            created_at: this.util.dateFormat(
                                ele.created_at,
                                moment_date_format.LLL
                            ),
                            refund_status: ele.refund_status.status
                        });
                    });
                    this.refundList = temp;
                }
            })
            .catch((error: HttpErrorResponse) => {
                this.serverError = error.error;
                this.response.success = false;
            });
        this.loading = false;
    }

    async fetch(refund_id: string): Promise<void> {
        this.util.resetResponse(this.response, this.error, this.serverError);
        this.loading = true;
        await this.patientRefundService
            .fetch(refund_id)
            .toPromise()
            .then((result: PromiseResult) => {
                this.response = result;
                if (result.success) {
                    this.refD = new refundData();
                    this.refD = result.data;
                    this.refD.data.audited_at = this.util.dateFormat(
                        result.data.data.audited_at,
                        moment_date_format.LLL
                    );
                    this.refD.data.updated_at = this.util.dateFormat(
                        result.data.data.updated_at,
                        moment_date_format.LLL
                    );
                    this.refD.data.created_at = this.util.dateFormat(
                        result.data.data.created_at,
                        moment_date_format.LLL
                    );
                    this.refD.data.p_cancellations.p_transactions.created_at =
                        this.util.dateFormat(
                            result.data.data.p_cancellations.p_transactions.created_at,
                            moment_date_format.LLL
                        );
                    this.details_dialog_bool = true;
                }
                this.loading = false;
            })
            .catch((error: HttpErrorResponse) => {
                this.serverError = error.error;
                this.response.success = false;
            });
        this.loading = false;
    }

    async sanction(body: refund_sanction_body): Promise<void> {
        this.util.resetResponse(this.response, this.error, this.serverError);
        this.loading = true;
        await this.patientRefundService
            .sanction(body)
            .toPromise()
            .then(async (result: PromiseResult) => {
                this.response = result;
                if (result.success) {
                    this.sanction_dialog_bool = false;
                    await this.fetchAll(null);
                    if (body.approve) {
                        this.messageService.add(
                            {
                                severity: 'success',
                                summary: 'Refunded',
                                detail: 'This transaction has been successfully refunded to the user.'
                            }
                        );
                    }
                    else {
                        this.messageService.add(
                            {
                                severity: 'info',
                                summary: 'Rejected',
                                detail: 'This request has been rejected successfully.'
                            }
                        );
                    }

                }
                else {
                    this.messageService.add(
                        {
                            severity: 'error',
                            summary: 'Error',
                            detail: 'Something went wrong.'
                        }
                    );
                }
            })
            .catch((error: HttpErrorResponse) => {
                this.serverError = error.error;
                this.response.success = false;
            });
        this.loading = false;
    }

    sanctionDialogAction(refund_id: string): void {
        this.sanction_dialog_bool = refund_id != null;
        this.sanction_body.refund_id = refund_id;
        this.sanction_body.approve = null;
    }
}
